import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faEye,
  faCheck,
  faTimes,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast } from "react-hot-toast";
import moment from "moment";
import { generateConfirm } from "../../helpers";
import useScrollTop from "../../hooks/useScrollTop";
import { ReviewsListing, ReviewStatus } from "../../services/reviews";
import { usePaginationState } from "../../hooks/usePaginationState";

const STORAGE_KEY = "reviewListPaginationState";

const ReviewList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Use the custom hook
  const [payload, setPayload] = usePaginationState(
    STORAGE_KEY,
    {
      page: 1,
      limit: 10,
      search_key: "",
    },
    ["/view-review"] // Paths where state should be preserved
  );

  const pageCount = Math.ceil(totalCount / payload.limit);
  useScrollTop();

  // Register custom navigation handler
  const handleViewReview = (reviewId, reviewType) => {
    navigate(`/view-review/${reviewId}`, {
      state: { reviewType: reviewType },
    });
  };

  // Fetch reviews from API
  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const response = await ReviewsListing(payload);
      if (response.data.statusCode === 200) {
        setReviews(response.data.result);
        setTotalCount(response.data.total_count);
      } else {
        toast.error("Failed to fetch reviews");
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Error fetching reviews");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [payload]);

  const handlePageClick = (e) => {
    setPayload({ ...payload, page: e.selected + 1 });
  };

  const handleSearch = () => {
    setPayload({ ...payload, search_key: searchTerm, page: 1 });
  };

  const getStatusText = (status) => {
    switch (status) {
      case "0":
        return "pending";
      case "1":
        return "approved";
      case "2":
        return "rejected";
      default:
        return "pending";
    }
  };

  const updateReviewStatus = async (reviewId, newStatus) => {
    try {
      const response = await ReviewStatus({
        review_id: reviewId.toString(),
        review_status: newStatus.toString(),
      });

      if (response.data.statusCode === 200) {
        setReviews(
          reviews.map((review) =>
            review.review_id === reviewId
              ? { ...review, review_status: newStatus.toString() }
              : review
          )
        );
        toast.success(`Review ${getStatusText(newStatus)} successfully`);
      } else {
        toast.error(`Failed to update review status`);
      }
    } catch (error) {
      console.error("Error updating review status:", error);
      toast.error("Error updating review status");
    }
  };

  const handleApprove = (reviewId) => {
    generateConfirm(
      "Are you sure you want to approve this review?",
      "Press Yes if you want to approve this review.",
      "review-approve",
      () => updateReviewStatus(reviewId, "1")
    );
  };

  const handleReject = (reviewId) => {
    generateConfirm(
      "Are you sure you want to reject this review?",
      "Press Yes if you want to reject this review.",
      "review-reject",
      () => updateReviewStatus(reviewId, "2")
    );
  };

  const formatDateTime = (dateString) => {
    return moment(dateString).format("YYYY-MM-DD hh:mm A");
  };

  return (
    <div className="dashboard review-list">
      <div className="subheader">
        <Row>
          <Col xs={5}>
            <h2 className="heading-36">Review List</h2>
          </Col>
          <Col xs={7}>
            <div className="subheader-right">
              <div className="subheader-right-top">
                <Form.Group controlId="formFile" className="subheader-search">
                  <Form.Control
                    type="search"
                    placeholder="Search by college or username"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="search-icon"
                    onClick={handleSearch}
                  />
                </Form.Group>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="table-main fixTableHead">
        {!isLoading ? (
          reviews.length > 0 ? (
            <table className="table nowrap table-bordered table-default-dark">
              <thead>
                <tr className="table-heading">
                  <th>S.No</th>
                  <th>College Name</th>
                  <th>Review Type</th>
                  <th>Username</th>
                  <th>Rating</th>
                  <th>Date and Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review, index) => (
                  <tr key={review.review_id}>
                    <td>{(payload.page - 1) * payload.limit + index + 1}</td>
                    <td
                      className="truncate-title"
                      title={review.clg_name || "N/A"}
                    >
                      {review.clg_name && review.clg_name.length > 20
                        ? review.clg_name.substring(0, 20) + "..."
                        : review.clg_name || "N/A"}
                    </td>

                    <td>{review.review_type}</td>
                    <td>{review.name}</td>
                    <td>
                      <div className="rating-stars">
                        {review.ratings.toFixed(1)}
                        <div className="stars">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`star ${
                                i < Math.floor(review.ratings) ? "filled" : ""
                              } ${
                                i === Math.floor(review.ratings) &&
                                review.ratings % 1 >= 0.5
                                  ? "half-filled"
                                  : ""
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td>{formatDateTime(review.created_at)}</td>
                    <td>{getStatusText(review.review_status)}</td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          className="action-btn view-btn"
                          onClick={() =>
                            handleViewReview(
                              review.review_id,
                              review.review_type
                            )
                          }
                          title="View"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>

                        <Button
                          className="action-btn approve-btn"
                          onClick={() => handleApprove(review.review_id)}
                          title="Approve"
                          disabled={review.review_status === "1"}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </Button>

                        <Button
                          className="action-btn reject-btn"
                          onClick={() => handleReject(review.review_id)}
                          title="Reject"
                          disabled={review.review_status === "2"}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <h1 className="no-data">No Reviews Found</h1>
          )
        ) : (
          <h1 className="no-data">Loading...</h1>
        )}
      </div>

      {totalCount > payload.limit && (
        <ReactPaginate
          previousLabel={<FontAwesomeIcon icon={faAngleDoubleLeft} />}
          nextLabel={<FontAwesomeIcon icon={faAngleDoubleRight} />}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={"pagination custom-pagination"}
          activeClassName={"active"}
          forcePage={payload.page - 1}
        />
      )}
    </div>
  );
};

export default ReviewList;
